import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { useAdminRoutes } from '@shared/lib/routes'

import {
  LESSON_NO_TUTOR_ID,
  LESSON_NO_TUTOR_OPTION,
  getTutorScheduleWeekFrom,
} from './lesson-create'

interface LessonTutorSelectOptions {
  tutorId: Ref<number | null>
  refOptions: ComputedRef<AppSelectOption<number>[]>
  /** 'YYYY-MM-DD' of the lesson: the calendar opens on its week. */
  refDate: ComputedRef<string>
  refIsLoading: ComputedRef<boolean>
}

interface LessonTutorSelectReturn {
  refTutorId: WritableComputedRef<number | undefined>
  refOptions: ComputedRef<AppSelectOption<number>[]>
  refScheduleUrl: ComputedRef<string>
  refIsScheduleDisabled: ComputedRef<boolean>
}

/** The tutor picker of every lesson form, with the legacy "-- Not selected --" row on top, and the link to the
 *  chosen tutor's week in the legacy tutor calendar. While the tutors are being read the dropdown offers nothing but
 *  its loader: the rows of the previous read must not be picked from. */
export const useLessonTutorSelect = ({ tutorId, refOptions, refDate, refIsLoading }: LessonTutorSelectOptions): LessonTutorSelectReturn => {
  const refTutorId = computed<number | undefined>({
    get: () => tutorId.value ?? LESSON_NO_TUTOR_ID,
    set: (value) => {
      tutorId.value = value === undefined || value === LESSON_NO_TUTOR_ID ? null : value
    },
  })

  const refOptionsWithNone = computed<AppSelectOption<number>[]>(() =>
    (refIsLoading.value ? [] : [LESSON_NO_TUTOR_OPTION, ...refOptions.value]))

  const refScheduleUrl = computed<string>(() => {
    if (!tutorId.value || !refDate.value) return ''

    return useAdminRoutes('admin.tutor-schedule.week', {
      from: getTutorScheduleWeekFrom(refDate.value),
      tutor_id: tutorId.value,
    }) ?? ''
  })

  const refIsScheduleDisabled = computed<boolean>(() => !tutorId.value || refIsLoading.value)

  return {
    refTutorId,
    refOptions: refOptionsWithNone,
    refScheduleUrl,
    refIsScheduleDisabled,
  }
}
